import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  MessageCircle,
  X,
  Send,
  Sparkles,
  ShieldCheck,
  Clock,
  Download,
  Paperclip,
  Loader2,
  CheckSquare,
  UserRound,
} from 'lucide-react'
import styles from './LiveChat.module.css'
import MagneticButton from './MagneticButton'
import { useAuth } from '../lib/auth'
import {
  createSupportTicket,
  replyToSupportTicket,
  fetchMyTickets,
  createTicketAttachment,
  listTicketAttachments,
  getTicketAttachmentUrl,
} from '../lib/support'
import { askDiya } from '../lib/diyaClient'

const STATUS_WORD = {
  open: 'Open',
  pending: 'Open',
  waiting: 'Waiting on you',
  resolved: 'Resolved',
  closed: 'Closed',
}

const DIYA_THREAD_KEY = 'opraiz:diya-thread'

const makeId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `diya-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

const LiveChat = () => {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [thread, setThread] = useState([])
  const [diyaThread, setDiyaThread] = useState([])
  const [ticket, setTicket] = useState(null)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [guest, setGuest] = useState({ name: '', email: '' })
  const [text, setText] = useState('')
  const [error, setError] = useState('')
  const [attachments, setAttachments] = useState([])
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef(null)
  const logRef = useRef(null)

  const loadAttachments = async (ticketId) => {
    if (!ticketId) {
      setAttachments([])
      return
    }
    try {
      const res = await listTicketAttachments(ticketId)
      setAttachments(res.attachments || [])
    } catch {
      setAttachments([])
    }
  }

  const loadLatest = async () => {
    try {
      const tickets = await fetchMyTickets()
      const openTicket =
        tickets.find((t) => t.status !== 'closed' && t.status !== 'resolved') || tickets[0]
      if (openTicket) {
        setTicket(openTicket)
        setThread(openTicket.support_messages || [])
        loadAttachments(openTicket.id)
      } else {
        setTicket(null)
        setThread([])
        setAttachments([])
        restoreDiyaThread()
      }
    } catch (e) {
      setError(e.message)
    }
  }

  useEffect(() => {
    if (open && user) {
      setLoading(true)
      loadLatest().finally(() => setLoading(false))
    }
    if (open && !user) {
      setTicket(null)
      setThread([])
      restoreDiyaThread()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user])

  useEffect(() => {
    logRef.current?.scrollTo?.({ top: logRef.current.scrollHeight })
  }, [thread.length, diyaThread.length, loading])

  useEffect(() => {
    if (!ticket && diyaThread.length > 0) {
      try {
        const stash = diyaThread
          .filter((m) => m.role === 'user' || m.role === 'diya')
          .slice(-20)
          .map(({ role, content }) => ({ role, content }))
        sessionStorage.setItem(DIYA_THREAD_KEY, JSON.stringify(stash))
      } catch {
        /* storage unavailable */
      }
    }
  }, [diyaThread, ticket])

  const clearDiyaSession = () => {
    try {
      sessionStorage.removeItem(DIYA_THREAD_KEY)
    } catch {
      /* storage unavailable */
    }
  }

  const restoreDiyaThread = () => {
    try {
      const raw = sessionStorage.getItem(DIYA_THREAD_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw)
      if (!Array.isArray(parsed)) return
      const valid = parsed.filter(
        (m) =>
          (m?.role === 'user' || m?.role === 'diya') &&
          typeof m?.content === 'string' &&
          m.content.trim()
      )
      if (valid.length === 0) return
      const now = new Date().toISOString()
      setDiyaThread(
        valid.map((m) => ({
          id: makeId(),
          role: m.role,
          content: m.content,
          created_at: now,
          handoff: false,
        }))
      )
    } catch {
      clearDiyaSession()
    }
  }

  const sendDiya = async (body) => {
    const history = diyaThread
      .filter((m) => m.role === 'user' || m.role === 'diya')
      .map(({ role, content }) => ({ role, content }))
    const userEntry = {
      id: makeId(),
      role: 'user',
      content: body,
      created_at: new Date().toISOString(),
      handoff: false,
    }
    const withUser = [...diyaThread, userEntry]
    setDiyaThread(withUser)
    setText('')
    try {
      const { reply, handoff } = await askDiya({ message: body, history })
      setDiyaThread((prev) => [
        ...prev,
        {
          id: makeId(),
          role: 'diya',
          content: reply,
          created_at: new Date().toISOString(),
          handoff,
        },
      ])
    } catch (err) {
      setError(err.message)
    }
  }

  const startTicket = async () => {
    setError('')
    const entries = diyaThread.filter((m) => m.role === 'user' || m.role === 'diya')
    const firstUser = entries.find((m) => m.role === 'user')?.content || ''
    const transcript =
      `Diya chat transcript — ${new Date().toLocaleString('en-IN')}\n` +
      entries
        .slice(-12)
        .map((m) => `${m.role === 'diya' ? 'Diya' : 'Visitor'}: ${m.content}\n`)
        .join('')
    const payload = user
      ? {
          name: user.user_metadata?.name,
          email: user.email,
          subject: firstUser.slice(0, 120),
          message: transcript,
        }
      : {
          name: guest.name,
          email: guest.email,
          subject: firstUser.slice(0, 120),
          message: transcript,
        }
    setLoading(true)
    try {
      const { ticket: t, thread: th } = await createSupportTicket(payload)
      setTicket(t)
      setThread(th || [])
      loadAttachments(t.id)
      setDiyaThread([])
      clearDiyaSession()
      setText('')
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  const escalate = async () => {
    if (sending || loading) return
    if (!user && !guest.email.trim()) {
      setError('Please enter your email to start the conversation.')
      return
    }
    await startTicket()
  }

  const send = async (e) => {
    e?.preventDefault()
    const body = text.trim()
    if (!body || sending) return
    setError('')
    setSending(true)

    if (ticket) {
      try {
        const res = await replyToSupportTicket({ ticket_id: ticket.id, message: body })
        setTicket(res.ticket)
        setThread(res.thread || [])
        setText('')
      } catch (err) {
        setError(err.message)
      }
    } else {
      try {
        await sendDiya(body)
      } finally {
        setSending(false)
      }
      return
    }
    setSending(false)
  }

  const close = () => setOpen(false)

  const downloadTranscript = (e) => {
    e?.stopPropagation()
    const lines = ticket
      ? thread.map(
          (m) =>
            `${new Date(m.created_at).toLocaleString('en-IN')} — ${m.author_type === 'team' ? 'OPRAIZ Team' : 'You'}:\n${m.body}\n`
        )
      : diyaThread
          .filter((m) => m.role === 'user' || m.role === 'diya')
          .map(
            (m) =>
              `${new Date(m.created_at).toLocaleString('en-IN')} — ${m.role === 'diya' ? 'Diya' : 'You'}:\n${m.content}\n`
          )
    const header = ticket
      ? `OPRAIZ live chat transcript\nTicket #${ticket.id.slice(0, 8)}\n${new Date().toLocaleString('en-IN')}\n\n`
      : `OPRAIZ live chat transcript\n${new Date().toLocaleString('en-IN')}\n\n`
    const blob = new Blob([header + lines.join('\n')], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `opraiz-chat-${Date.now()}.txt`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const onUpload = async (ev) => {
    const file = ev.target.files?.[0]
    ev.target.value = ''
    if (!file) return
    if (!ticket) {
      setError('Start a conversation first, then attach files.')
      return
    }
    setError('')
    setUploading(true)
    const reader = new FileReader()
    reader.onload = async () => {
      try {
        const res = await createTicketAttachment({
          ticket_id: ticket.id,
          filename: file.name,
          mime: file.type || 'application/octet-stream',
          base64: String(reader.result),
        })
        if (res.attachment) setAttachments((prev) => [...prev, res.attachment])
      } catch (err) {
        setError(err.message)
      } finally {
        setUploading(false)
      }
    }
    reader.onerror = () => {
      setUploading(false)
      setError('Could not read that file.')
    }
    reader.readAsDataURL(file)
  }

  const openAttachment = async (att) => {
    if (!ticket) return
    try {
      const res = await getTicketAttachmentUrl(ticket.id, att.id)
      if (res?.url) window.open(res.url, '_blank', 'noopener,noreferrer')
    } catch {
      setError('Could not open that file.')
    }
  }

  const handoffOffered = diyaThread.some((m) => m.role === 'diya' && m.handoff)
  const diyaActive = !ticket && !loading && diyaThread.length > 0
  const showAttach = ticket || !diyaActive

  return createPortal(
    <>
      <MagneticButton className={styles.fabWrap} strength={0.22}>
        <button
          className={styles.fab}
          onClick={() => setOpen((v) => !v)}
          aria-label="Live chat"
          aria-expanded={open}
        >
          {open ? <X size={20} /> : <MessageCircle size={20} />}
        </button>
      </MagneticButton>

      {open && (
        <div
          className={styles.panel}
          role="dialog"
          aria-label="OPRAIZ live chat"
          onKeyDown={(e) => {
            if (e.key === 'Escape') close()
          }}
        >
          <div className={styles.header}>
            <div className={styles.avatar}>
              <Sparkles size={16} />
            </div>
            <div className={styles.headerBody}>
              <strong>OPRAIZ Live Help</strong>
              <span>
                <span className={styles.onlineDot} /> Diya replies instantly — the human team joins
                when needed
              </span>
            </div>
            <button
              className={`${styles.closeBtn} ${styles.ghostBtn}`}
              onClick={downloadTranscript}
              aria-label="Download transcript"
            >
              <Download size={15} />
            </button>
            <button className={styles.closeBtn} onClick={close} aria-label="Close chat">
              <X size={15} />
            </button>
          </div>

          <div
            className={styles.log}
            ref={logRef}
            role="log"
            aria-live="polite"
            aria-relevant="additions"
          >
            {!ticket && !loading && diyaThread.length === 0 && (
              <div className={`${styles.bubble} ${styles.bubbleIn} ${styles.diyaMsg}`}>
                <span className={styles.tagDiya}>Diya</span>
                <p>
                  Hi, I'm Diya — OPRAIZ's Live Help assistant. We reply fast during business hours
                  (Mon–Sat), and I reply right away.
                </p>
              </div>
            )}

            {loading && (
              <div className={`${styles.bubble} ${styles.bubbleIn}`}>
                <span className={styles.typing}>
                  <i />
                  <i />
                  <i />
                </span>
              </div>
            )}

            {!loading &&
              thread.map((m) => (
                <div
                  key={m.id}
                  className={`${styles.bubble} ${m.author_type === 'team' ? styles.bubbleIn : styles.bubbleOut}`}
                >
                  <p>{m.body}</p>
                  <span>
                    {m.author_type === 'team' ? 'OPRAIZ Team' : 'You'} ·{' '}
                    {new Date(m.created_at).toLocaleTimeString('en-IN', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              ))}

            {!loading &&
              !ticket &&
              diyaThread.map((m) =>
                m.role === 'diya' ? (
                  <div
                    key={m.id}
                    className={`${styles.bubble} ${styles.bubbleIn} ${styles.diyaMsg}`}
                  >
                    <span className={styles.tagDiya}>Diya</span>
                    <p>{m.content}</p>
                    <span>
                      {new Date(m.created_at).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                ) : (
                  <div key={m.id} className={`${styles.bubble} ${styles.bubbleOut}`}>
                    <p>{m.content}</p>
                    <span>
                      {new Date(m.created_at).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                )
              )}

            {!loading && ticket && thread.length === 0 && (
              <div className={`${styles.bubble} ${styles.bubbleIn}`}>
                <p>
                  Your ticket <strong>#{ticket.id.slice(0, 8)}</strong> is{' '}
                  {STATUS_WORD[ticket.status].toLowerCase()}. Type below to add a reply.
                </p>
              </div>
            )}

            {!loading && diyaActive && handoffOffered && (
              <button
                type="button"
                className={styles.handoffBtn}
                aria-label="Ask the human team"
                onClick={escalate}
                disabled={sending}
              >
                <CheckSquare size={14} /> Ask the human team
              </button>
            )}

            {error && (
              <div className={styles.error} role="alert">
                {error}
              </div>
            )}

            {!loading && attachments.length > 0 && (
              <div className={styles.attachments}>
                <span className={styles.attachLabel}>
                  <Paperclip size={11} /> Files
                </span>
                {attachments.map((att) => (
                  <button
                    key={att.id}
                    className={styles.attachChip}
                    onClick={() => openAttachment(att)}
                  >
                    <Paperclip size={12} /> {att.filename} · {(att.size / 1024).toFixed(0)} KB
                  </button>
                ))}
              </div>
            )}
          </div>

          {!user && ticket === null && !loading && (
            <div className={styles.guest}>
              <input
                value={guest.name}
                onChange={(e) => setGuest({ ...guest, name: e.target.value })}
                placeholder="Your name (optional)"
                aria-label="Your name"
                autoComplete="name"
              />
              <input
                type="email"
                value={guest.email}
                onChange={(e) => setGuest({ ...guest, email: e.target.value })}
                placeholder="Email address"
                aria-label="Email address"
                autoComplete="email"
              />
            </div>
          )}

          <form className={styles.input} onSubmit={send}>
            <input
              type="file"
              ref={fileRef}
              onChange={onUpload}
              className={styles.hiddenInput}
              tabIndex={-1}
              aria-hidden="true"
            />
            {showAttach && (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                aria-label="Attach a file"
                className={styles.attachBtn}
              >
                {uploading ? (
                  <Loader2 size={15} className={styles.spin} />
                ) : (
                  <Paperclip size={15} />
                )}
              </button>
            )}
            <input
              className={styles.messageInput}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={ticket ? 'Reply to ticket…' : 'Type your message…'}
              aria-label="Chat message"
            />
            <button type="submit" disabled={!text.trim() || sending} aria-label="Send message">
              <Send size={15} />
            </button>
          </form>

          {!ticket && !loading && diyaThread.length > 0 && (
            <div className={styles.miniRow}>
              <button
                type="button"
                className={styles.handoffMini}
                aria-label="Talk to a human"
                onClick={escalate}
                disabled={sending}
              >
                <UserRound size={11} /> Talk to a human
              </button>
            </div>
          )}

          <div className={styles.foot}>
            <span>
              <ShieldCheck size={11} /> Encrypted
            </span>
            <span>
              <Clock size={11} /> SLA-aware queue
            </span>
          </div>
        </div>
      )}
    </>,
    document.body
  )
}

export default LiveChat
